import {
  BODY_FONT,
  HEADER_FILL,
  LINK_FONT,
  NA_FILL,
  THIN,
  THIN_BLACK,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
} from "./detailSheetCommon.js";

export {
  BODY_FONT,
  HEADER_FILL,
  LINK_FONT,
  NA_FILL,
  THIN_BLACK,
  TITLE_FONT,
  VALUE_FILL,
  hideRowsThrough,
};

/** Formula / GD-linked cells (reference FFBEBEBE). */
export const LINKED_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFBEBEBE" },
};

/** Kept for stakeholderGroupSheet and other legacy detail sheets. */
export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

export const SUBHEADER_FILL = NA_FILL;

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;
export const COL_M = 13;
export const COL_N = 14;
export const COL_O = 15;
export const COL_P = 16;
export const COL_END = COL_P;

export const BENEFIT_KEYS = [
  ["healthInsurance", "Health insurance"],
  ["accidentInsurance", "Accident insurance"],
  ["maternityBenefits", "Maternity benefits"],
  ["paternityBenefits", "Paternity Benefits"],
  ["dayCareFacilities", "Day Care facilities"],
];

const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

export function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

export function borderFromSpecs(specs, col, colStart = COL_D) {
  const spec = specs?.[col - colStart];
  return spec ? parseBorderSpec(spec) : THIN_BLACK;
}

export function applyBorderSpecs(ws, row, specs, colStart = COL_D) {
  if (!specs?.length) return;
  specs.forEach((spec, index) => {
    const cell = ws.getCell(row, colStart + index);
    cell.border = parseBorderSpec(spec);
    if (!cell.font) cell.font = BODY_FONT;
  });
}

/** Full-width D:P outer band: left medium, top/bottom thin, right medium on P. */
export function dpTitleBorders() {
  return ["mmt-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-tm"];
}

export function dpSectionBorders() {
  return ["tmt-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-tm"];
}

export function dpEdgeOnly() {
  return Array.from({ length: 13 }, (_, i) => (i === 12 ? "---m" : "----"));
}

export function outerMergeBorders(endCol, { first = "tmt-", middle = "t-t-", last = "t-tt", edge = "---m" } = {}) {
  const specs = [];
  for (let col = COL_D; col <= COL_P; col += 1) {
    if (col > endCol && col < COL_P) specs.push("----");
    else if (col === COL_P) specs.push(edge);
    else if (col === COL_D) specs.push(first);
    else if (col === endCol) specs.push(last);
    else specs.push(middle);
  }
  return specs;
}

export function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "Add Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else if (options.formula) {
    cell.value = { formula: options.formula };
    cell.font = options.font || (options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT);
  } else {
    cell.value = value;
    if (options.font) {
      cell.font =
        options.bold !== undefined ? { ...options.font, bold: options.bold } : options.font;
    } else {
      cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
    }
  }

  if (options.border) cell.border = options.border;
  else if (options.bordered !== false) cell.border = THIN_BLACK;

  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) cell.fill = options.fill;
}

export function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    if (options.bordered !== false && !options.border) cell.border = THIN_BLACK;
  }
}

export function padMergeRow(ws, row, startCol, endCol, specs) {
  for (let col = startCol + 1; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    if (cell.value === null || cell.value === undefined) cell.value = "";
  }
  if (startCol < endCol) {
    ws.mergeCells(row, startCol, row, endCol);
  }
  if (!specs?.length) return;
  // ExcelJS keeps one border on the master for the whole merge — set the outer box.
  const startBorder = borderFromSpecs(specs, startCol) || {};
  const endBorder = borderFromSpecs(specs, endCol) || {};
  const master = ws.getCell(row, startCol);
  master.border = {
    top: startBorder.top || endBorder.top,
    left: startBorder.left,
    bottom: startBorder.bottom || endBorder.bottom,
    right: endBorder.right || startBorder.right,
  };
}

export function mergeBlock(ws, startRow, startCol, endRow, endCol, value, options = {}) {
  if (startRow !== endRow || startCol !== endCol) {
    ws.mergeCells(startRow, startCol, endRow, endCol);
  }
  setCell(ws, startRow, startCol, value, options);
}

export function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

export function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  if (value === "NA" || value === "N.A." || value === "na") return "NA";
  return "";
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).trim();
  if (!str) return "";
  if (str.includes("%")) return str;
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  return String(num);
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : String(num);
  }
  return String(value);
}

export function formatRatio(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).replace("%", "").trim();
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  const ratio = Math.abs(num) > 1 ? num / 100 : num;
  const fixed = ratio.toFixed(4);
  return fixed.replace(/\.?0+$/, "") || "0";
}

export function setPrinciple3ColumnWidths(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 31.7265625,
    E: 15.7265625,
    F: 15.7265625,
    G: 15.7265625,
    H: 15.7265625,
    I: 15.7265625,
    J: 15.7265625,
    K: 15.7265625,
    L: 15.7265625,
    M: 15.7265625,
    N: 15.7265625,
    O: 15.7265625,
    P: 15.7265625,
    Q: 9.1796875,
    R: 9.1796875,
    S: 9.1796875,
    T: 1.81640625,
    U: 1.81640625,
    V: 1.81640625,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ["A", "B"].forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
  for (let col = 20; col <= 16384; col += 1) {
    ws.getColumn(col).hidden = true;
  }
}

export function setupPrinciple3SheetView(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 3,
      ySplit: 8,
      topLeftCell: "D9",
      zoomScale: 80,
      zoomScaleNormal: 80,
    },
  ];
  for (let row = 1; row <= 5; row += 1) {
    ws.getRow(row).hidden = true;
  }
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
}

export function setWhite(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    fill: VALUE_FILL,
    bordered: false,
    border,
  });
}

export function setLinked(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    fill: LINKED_FILL,
    bordered: false,
    border,
  });
}

/** Value cell with no background (reference formula/% columns). */
export function setPlain(ws, row, col, value, border, align = "center") {
  setCell(ws, row, col, value ?? "", {
    align,
    bordered: false,
    border,
  });
}

export function setGrey(ws, row, col, border) {
  setCell(ws, row, col, "", {
    fill: NA_FILL,
    bordered: false,
    border,
  });
}

export function setEdgeP(ws, row, spec = "---m", { grey = false } = {}) {
  setCell(ws, row, COL_P, "", {
    fill: grey ? NA_FILL : undefined,
    bordered: false,
    border: parseBorderSpec(spec),
  });
}

/** Paint K:P (or startCol:P) with NA grey — spending / trailing buffer. */
export function setGreyRange(ws, row, startCol, endCol = COL_P, borderSpec = "----") {
  for (let col = startCol; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = NA_FILL;
    if (!cell.font) cell.font = BODY_FONT;
    if (borderSpec && !cell.border) cell.border = parseBorderSpec(borderSpec);
  }
}
